import { ApiPipelineStage } from "@/types/recruit/stages.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  EmojiDisplay,
  FontAwesomeIcon,
  Stack,
} from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";

export interface StagesFunnelSelectorProps {
  stages: ApiPipelineStage[];
  startStageId: number | null | undefined;
  endStageId: number | null | undefined;
  onChange: (startStageId: number, endStageId: number) => void;
}

export function StagesFunnelSelector({
  stages,
  startStageId,
  endStageId,
  onChange,
}: StagesFunnelSelectorProps) {
  const effectiveStartStageId = useMemo(() => {
    if (startStageId != null) return startStageId;
    return stages.length > 0 ? stages[0].id : null;
  }, [startStageId, stages]);

  const effectiveEndStageId = useMemo(() => {
    if (endStageId != null) return endStageId;
    return stages.length > 0 ? stages[stages.length - 1].id : null;
  }, [endStageId, stages]);

  useEffect(() => {
    if (
      effectiveStartStageId != null &&
      effectiveEndStageId != null &&
      (startStageId == null || endStageId == null)
    ) {
      onChange(effectiveStartStageId, effectiveEndStageId);
    }
  }, [effectiveStartStageId, effectiveEndStageId, startStageId, endStageId, onChange]);

  const startStageIndex = useMemo(() => {
    return stages.findIndex((s) => s.id === effectiveStartStageId);
  }, [stages, effectiveStartStageId]);

  const endStageIndex = useMemo(() => {
    return stages.findIndex((s) => s.id === effectiveEndStageId);
  }, [stages, effectiveEndStageId]);

  const selectedStartStage = useMemo(() => {
    return stages.find((s) => s.id === effectiveStartStageId);
  }, [stages, effectiveStartStageId]);

  const selectedEndStage = useMemo(() => {
    return stages.find((s) => s.id === effectiveEndStageId);
  }, [stages, effectiveEndStageId]);

  const handleStartStageChange = (stageId: number) => {
    const newStartIndex = stages.findIndex((s) => s.id === stageId);
    if (newStartIndex >= endStageIndex && effectiveEndStageId != null) {
      const nextEndStage = stages[newStartIndex + 1];
      if (nextEndStage) {
        onChange(stageId, nextEndStage.id);
      }
    } else if (effectiveEndStageId != null) {
      onChange(stageId, effectiveEndStageId);
    }
  };

  const handleEndStageChange = (stageId: number) => {
    const newEndIndex = stages.findIndex((s) => s.id === stageId);
    if (newEndIndex <= startStageIndex && effectiveStartStageId != null) {
      const prevStartStage = stages[newEndIndex - 1];
      if (prevStartStage) {
        onChange(prevStartStage.id, stageId);
      }
    } else if (effectiveStartStageId != null) {
      onChange(effectiveStartStageId, stageId);
    }
  };

  const availableEndStages = useMemo(() => {
    return stages.filter((_, index) => index > startStageIndex);
  }, [stages, startStageIndex]);

  const availableStartStages = useMemo(() => {
    return stages.filter((_, index) => index < endStageIndex);
  }, [stages, endStageIndex]);

  return (
    <Stack direction="horizontal" gap="sm" items="center">
      <DropdownMenu
        trigger={
          <Button
            variant="secondary"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down shrink-0" />}
          >
            {selectedStartStage?.title || "-"}
          </Button>
        }
      >
        {availableStartStages.map((stage) => (
          <DropdownMenuItem
            key={`start-stage-${stage.id}`}
            title={stage.title}
            isSelected={stage.id === effectiveStartStageId}
            onClick={() => handleStartStageChange(stage.id)}
          />
        ))}
      </DropdownMenu>

      <FontAwesomeIcon icon="fa-light fa-arrow-right" className="text-secondary" />

      <DropdownMenu
        trigger={
          <Button
            variant="secondary"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down shrink-0" />}
          >
            {selectedEndStage?.title || "-"}
          </Button>
        }
      >
        {availableEndStages.map((stage) => (
          <DropdownMenuItem
            key={`end-stage-${stage.id}`}
            leftIcon={stage.emoji ? <EmojiDisplay size="sm" id={stage.emoji} /> : undefined}
            title={stage.title}
            isSelected={stage.id === effectiveEndStageId}
            onClick={() => handleEndStageChange(stage.id)}
          />
        ))}
      </DropdownMenu>
    </Stack>
  );
}
